import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArchiveRestore, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from '@/components/ui/toaster';
import { useHasRole } from '@/features/auth/AuthProvider';
import {
  fetchArchivedInventory,
  restoreInventoryItem,
  deleteInventoryItem,
} from '../inventoryQueries';
import {
  fetchArchivedCategories,
  restoreCategory,
  deleteCategory,
} from '../setupQueries';
import {
  fetchArchivedSuppliers,
  restoreSupplier,
  deleteSupplier,
} from '../supplierQueries';

type SubTab = 'products' | 'categories' | 'suppliers';

export function ArchivedTab() {
  const qc = useQueryClient();
  const canDelete = useHasRole('owner');
  const [subTab, setSubTab] = useState<SubTab>('products');

  const { data: products = [] } = useQuery({
    queryKey: ['inventory-archived'],
    queryFn: fetchArchivedInventory,
    enabled: subTab === 'products',
  });
  const { data: categories = [] } = useQuery({
    queryKey: ['categories-archived'],
    queryFn: fetchArchivedCategories,
    enabled: subTab === 'categories',
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers-archived'],
    queryFn: fetchArchivedSuppliers,
    enabled: subTab === 'suppliers',
  });

  const restore = useMutation({
    mutationFn: async ({ type, id }: { type: SubTab; id: string }) => {
      if (type === 'products') await restoreInventoryItem(id);
      else if (type === 'categories') await restoreCategory(id);
      else await restoreSupplier(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory'] });
      qc.invalidateQueries({ queryKey: ['inventory-archived'] });
      qc.invalidateQueries({ queryKey: ['inventory-categories'] });
      qc.invalidateQueries({ queryKey: ['categories-archived'] });
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      qc.invalidateQueries({ queryKey: ['suppliers-archived'] });
      toast({ title: 'Restored', variant: 'success' });
    },
  });

  const hardDelete = useMutation({
    mutationFn: async ({ type, id }: { type: SubTab; id: string }) => {
      if (type === 'products') await deleteInventoryItem(id);
      else if (type === 'categories') await deleteCategory(id);
      else await deleteSupplier(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inventory-archived'] });
      qc.invalidateQueries({ queryKey: ['categories-archived'] });
      qc.invalidateQueries({ queryKey: ['suppliers-archived'] });
      toast({ title: 'Deleted permanently', variant: 'success' });
    },
    onError: (e) => toast({ title: 'Delete failed', description: String(e), variant: 'destructive' }),
  });

  const items =
    subTab === 'products' ? products : subTab === 'categories' ? categories : suppliers;

  return (
    <>
      <div className="mb-4 flex gap-2">
        {(['products', 'categories', 'suppliers'] as SubTab[]).map((t) => (
          <Button
            key={t}
            size="sm"
            variant={subTab === t ? 'default' : 'outline'}
            onClick={() => setSubTab(t)}
          >
            {t}
          </Button>
        ))}
      </div>
      <div className="space-y-2">
        {items.length === 0 && (
          <p className="text-sm text-muted-foreground p-8 text-center">Nothing archived.</p>
        )}
        {subTab === 'products' &&
          products.map((p) => (
            <Card key={p.id} className="flex items-center justify-between p-3">
              <span>{p.sku} — {p.brand}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => restore.mutate({ type: 'products', id: p.id })}>
                  <ArchiveRestore className="h-3.5 w-3.5" />
                </Button>
                {canDelete && (
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => hardDelete.mutate({ type: 'products', id: p.id })}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        {subTab === 'categories' &&
          categories.map((c) => (
            <Card key={c.id} className="flex items-center justify-between p-3">
              <span>{c.name} ({c.codePrefix})</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => restore.mutate({ type: 'categories', id: c.id })}>
                  <ArchiveRestore className="h-3.5 w-3.5" />
                </Button>
                {canDelete && (
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => hardDelete.mutate({ type: 'categories', id: c.id })}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
        {subTab === 'suppliers' &&
          suppliers.map((s) => (
            <Card key={s.id} className="flex items-center justify-between p-3">
              <span>{s.name}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => restore.mutate({ type: 'suppliers', id: s.id })}>
                  <ArchiveRestore className="h-3.5 w-3.5" />
                </Button>
                {canDelete && (
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => hardDelete.mutate({ type: 'suppliers', id: s.id })}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
            </Card>
          ))}
      </div>
    </>
  );
}
